import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';
import { useIdentity } from 'base/identity-resolver';
import Api from 'entities/helpers/api';
import EverTrue from 'app';
import PropTypes from 'prop-types';

const ThankViewAuthContext = createContext(null);

// Refresh a bit before the token actually expires so an in-flight Signal API
// call never races the expiry.
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

const readStoredAuth = () => {
  try {
    return JSON.parse(localStorage.getItem('thankview-auth')) || {};
  } catch {
    return {};
  }
};

// Single module-level store, shared by every ThankViewAuthProvider instance
// on the page - not just an in-flight-request flag. Confirmed via live
// debugging that this app legitimately mounts several independent instances
// at once (every open email drawer gets its own via EmailForm, up to 3
// concurrently; every THANK_VIEW task row gets its own too), each auto-
// requesting a token on mount via ThankviewSettingsProvider. Per-instance
// React state can't dedupe across these: each instance only ever saw
// localStorage as it was at ITS OWN mount time, so one instance minting a
// fresh token was invisible to a sibling instance that had mounted moments
// earlier and already snapshotted a stale "no token yet" state. Centralizing
// token/expiry/in-flight state here means every instance reads and writes
// the exact same source of truth, so a token minted by one is immediately
// visible to all the others - closing the race instead of narrowing it.
const initialAuth = readStoredAuth();
let state = {
  token: initialAuth.token ?? null,
  expiresAt: initialAuth.expires_at ?? null,
  thankViewUserId: initialAuth.user_id ?? null,
  isAuthenticating: false,
};
let inFlightConnectPromise = null;
const listeners = new Set();

const setState = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

// No name/email/et_oid/et_user_id/business_name is sent here: the auth
// service derives all of that from the caller's own authenticated session,
// so this browser never needs to hold (or send) anything Thankview treats
// as an identity credential.
const authenticateUser = (orgId) => {
  if (inFlightConnectPromise) {
    return inFlightConnectPromise;
  }

  EverTrue.track.set('signal_video', { type: 'signal_video_register_user' });
  setState({ isAuthenticating: true });

  inFlightConnectPromise = (async () => {
    try {
      const response = await Api.AUTH.THANKVIEW_CONNECT.post({ params: { oid: orgId } });
      const { token: newToken, expires_at: newExpiresAt, user: thankViewUser } = response.data;

      setState({ token: newToken, expiresAt: newExpiresAt, thankViewUserId: thankViewUser.id });
      localStorage.setItem(
        'thankview-auth',
        JSON.stringify({ token: newToken, expires_at: newExpiresAt, user_id: thankViewUser.id })
      );

      return newToken;
    } catch (error) {
      console.error('Error:', error);
      return null;
    } finally {
      setState({ isAuthenticating: false });
      inFlightConnectPromise = null;
    }
  })();

  return inFlightConnectPromise;
};

const getValidToken = (orgId) => {
  const { token, expiresAt } = state;
  if (token && expiresAt && new Date(expiresAt).getTime() - Date.now() > TOKEN_REFRESH_BUFFER_MS) {
    return Promise.resolve(token);
  }
  return authenticateUser(orgId);
};

// Test-only: the store above is seeded once, at module load, to mirror a
// real page load. Tests reuse the same module across many cases, so this
// resets it back to a fresh-load state (re-reading whatever localStorage
// currently holds) instead of leaking state between them.
export const __resetThankViewAuthForTests = () => {
  const fresh = readStoredAuth();
  state = {
    token: fresh.token ?? null,
    expiresAt: fresh.expires_at ?? null,
    thankViewUserId: fresh.user_id ?? null,
    isAuthenticating: false,
  };
  inFlightConnectPromise = null;
  listeners.clear();
};

const ThankViewAuthProvider = ({ children }) => {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  const { org } = useIdentity();

  const boundGetValidToken = useCallback(() => getValidToken(org.id), [org.id]);

  const value = {
    thankViewUserId: snapshot.thankViewUserId,
    isAuthenticating: snapshot.isAuthenticating,
    getValidToken: boundGetValidToken,
  };

  return <ThankViewAuthContext.Provider value={value}>{children}</ThankViewAuthContext.Provider>;
};

const useThankViewAuth = () => {
  const context = useContext(ThankViewAuthContext);
  if (context === null) {
    throw new Error('useThankViewAuth must be used within a ThankViewAuthProvider');
  }
  return context;
};

ThankViewAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { ThankViewAuthProvider, useThankViewAuth };
